import { connect } from 'react-redux';
import { css } from 'styled-components';
import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { ICON_TYPES } from 'enums/configuration';
import { trackSearchResultSelected } from 'events/analytics';
import CustomIcon from 'presentation/components/icons/CustomIcon';
import overrideStyles from 'lib/overrideStyle';
import updateSelectedAnswer from 'actions/selfService/updateSelectedAnswer';

export function SearchResultsItem({ answerId, index, isFocused, name, onClick, onSetFocused, scrollIntoView }) {
  const ref = useRef(null);
  const handleOnClick = useCallback(() => {
    onClick({ answerId, answerName: name, position: index + 1 });
  }, [answerId, name]);
  const handleKeyDown = useCallback(
    ev => {
      if (ev.key === 'Enter') {
        onClick({ answerId, answerName: name, position: index + 1 });
      }
    },
    [answerId, name]
  );

  const isHoveredRef = useRef(false);
  const setFocused = useCallback(() => {
    isHoveredRef.current = true;
    onSetFocused(index);
  }, [index]);
  const onMouseLeave = useCallback(() => {
    isHoveredRef.current = false;
  }, []);

  useEffect(() => {
    if (isFocused && !isHoveredRef.current) {
      scrollIntoView(ref.current.getBoundingClientRect());
    }
  }, [isFocused]);

  return (
    <StyledSearchResultItem
      aria-label={name}
      data-aid="selfService-searchResult-item"
      isFocused={isFocused}
      onFocus={setFocused}
      onKeyDown={handleKeyDown}
      onMouseLeave={onMouseLeave}
      onMouseOver={setFocused}
      onClick={handleOnClick}
      ref={ref}
      role="button"
      tabIndex="0"
    >
      <NameWrapper dangerouslySetInnerHTML={{ __html: name }} title={name} />
      <ChevronWrapper>
        <CustomIcon type={ICON_TYPES.INTERNAL_LINK} />
      </ChevronWrapper>
    </StyledSearchResultItem>
  );
}

SearchResultsItem.propTypes = {
  answerId: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  isFocused: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onSetFocused: PropTypes.func.isRequired,
  scrollIntoView: PropTypes.func.isRequired,
};

const ChevronWrapper = overrideStyles('div')`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  transition: transform 300ms ease;
  svg {
    cursor: pointer;
  }
`;

const StyledSearchResultItem = overrideStyles('div')`
  align-items: center;
  cursor: pointer;
  display: flex;
  font-family: ${p => p.theme.fontLibrary.secondary};
  font-size: 14px;
  justify-content: space-between;
  padding: 16px 32px;
  &:active, &:focus {
    outline: none;
  }
  ${p => p.isFocused && highlighted}
`;

const highlighted = css`
  ${ChevronWrapper} {
    transform: translateX(10px) !important;
  }
`;

const NameWrapper = overrideStyles('div')`
  cursor: pointer;
  flex: 1 1 auto;
  height: 15px;
  margin-right: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  mark {
    background-color: white;
    cursor: pointer;
    font-style: normal;
    font-weight: bold;
    white-space: nowrap;
  }
`;

export const mapDispatchToProps = dispatch => {
  return {
    onClick: ({ answerId, answerName, position }) => {
      dispatch(trackSearchResultSelected({ entityId: answerId, entityType: 'answer', label: answerName, position }));
      dispatch(updateSelectedAnswer(answerId, answerName));
    },
  };
};

const SearchResultsItemContainer = connect(
  null,
  mapDispatchToProps
)(SearchResultsItem);

export default SearchResultsItemContainer;
